<script setup lang="ts">
  import { computed } from 'vue';

  import { SelectInput, Tooltip, IconImage, ColorModeToggle } from '@/shared/ui';
  import MenuFriesLeft from '@/shared/assets/icons/MenuFriesLeftIcon.svg';
  import MenuFriesRight from '@/shared/assets/icons/MenuFriesRightIcon.svg';
  import NotificationIcon from '@/shared/assets/icons/NotificationIcon.svg';
  import type { CaslAction, CaslSubject } from '@/shared/lib/casl';

  interface Props {
    isCollapsed: boolean;
    branches: { id: number; name: string }[];
    selectedBranch?: string;
    selectedNavigation?: string;
    unreadNoticeCount?: number;
    canCheck?: (action: CaslAction, subject: CaslSubject) => boolean;
  }

  interface Emits {
    (e: 'toggle-sidebar'): void;
    (e: 'open-notification'): void;
    (e: 'update:selected-branch', value: string): void;
    (e: 'update:selected-navigation', value: string): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    selectedBranch: '',
    selectedNavigation: '',
    unreadNoticeCount: 0,
  });

  const emit = defineEmits<Emits>();

  const localSelectedBranch = computed({
    get: () => props.selectedBranch,
    set: (value: string) => emit('update:selected-branch', value),
  });

  const localSelectedNavigation = computed({
    get: () => props.selectedNavigation,
    set: (value: string) => emit('update:selected-navigation', value),
  });

  const branchOptions = computed(() =>
    props.branches.map((branch) => ({
      id: branch.id,
      name: branch.name,
      value: branch.id.toString(),
    }))
  );

  const navigationOptions = [
    { id: 'user', name: 'Usuário' },
    { id: 'analyst', name: 'Analista' },
    { id: 'admin', name: 'Administrador' },
  ];

  // "Navegar como" (impersonateUser:Admin)
  const canImpersonate = computed(
    () => !props.canCheck || props.canCheck('impersonateUser', 'Admin')
  );

  const handleToggleSidebar = () => {
    emit('toggle-sidebar');
  };

  const handleOpenNotification = () => {
    emit('open-notification');
  };

  const unreadBadgeLabel = computed(() => {
    const count = props.unreadNoticeCount ?? 0;
    return count > 9 ? '9+' : String(count);
  });
</script>

<template>
  <nav class="h-full w-full px-6 py-4 bg-white-01">
    <div class="flex items-center text-sm gap-6">
      <button class="h-10.5 w-10.5 px-3 border-container rounded-lg" @click="handleToggleSidebar">
        <IconImage
          :src="props.isCollapsed ? MenuFriesRight : MenuFriesLeft"
          alt="colapsar"
          class="w-4"
          color="var(--theme-secondary-ink)"
        />
      </button>

      <div v-if="canImpersonate" class="flex items-center gap-6">
        <SelectInput
          v-model="localSelectedNavigation"
          :options="navigationOptions"
          label="Navegar como"
          class="min-w-[320px]"
        />
      </div>

      <div class="flex items-center space-x-4 ml-auto">
        <ColorModeToggle />
        <Tooltip content="Notificações" placement="bottom">
          <button
            class="relative flex h-11 w-11 items-center justify-center rounded-full border border-gray-02"
            @click="handleOpenNotification"
          >
            <IconImage
             :src="NotificationIcon" 
             alt="Notificação" 
             color="var(--color-gray-07)" />
            <span
              v-if="(props.unreadNoticeCount ?? 0) > 0"
              class="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-16 dark:bg-red-700 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {{ unreadBadgeLabel }}
            </span>
          </button>
        </Tooltip>
      </div>
    </div>
  </nav>
</template>

<style scoped>
  .border-container {
    border: 1px solid var(--color-border-color);
  }
</style>
